import { css } from '@emotion/react';
import React, { useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import { AngularContext } from 'react-app';
import t from 'react-translate';
import GradientAiIcon from 'lecture_pages/components/left-panel/novo-ai/components/shared/gradient-ai-icon';
import { config } from '@config/pendo.config.json';
import { savePersonalizedTakeawaysSettings } from 'redux/actions/help-options';
import { useAppDispatch } from 'redux/store';
import NvSwitch from 'shared/components/inputs/nv-switch';
import NvModal, { ModalType } from 'shared/components/nv-modal';
import NvIcon from 'shared/components/nv-icon';
import {
  gray2,
  primary,
  warning,
} from 'styles/global_defaults/colors';
import { doubleSpacing, halfSpacing, standardSpacing } from 'styles/global_defaults/scaffolding';

type PersonalizedTakeawaysSettingsModalProps = {
  show: boolean;
  onClose: () => void;
  isAiCapabilitiesEnabled: boolean;
  initialEnabled: boolean;
  catalogId: string;
  courseAlias: string;
  onSave: (enabled: boolean) => void;
};

const modalBodyStyles = css`
  .header-row {
    align-items: center;
    display: flex;
    justify-content: center;
  }

  .description {
    color: ${gray2};
    margin-bottom: 0;
    padding: ${standardSpacing}px 0;
  }

  .disabled-alert {
    align-items: center;
    display: flex;
    padding-top: ${halfSpacing}px;
  }

  .disabled-alert .icon {
    color: ${warning};
  }

  .switch-row {
    align-items: center;
    color: ${gray2};
    display: flex;
    margin-top: 0;
  }

  .example-link {
    color: ${primary};
    display: inline-block;
    margin-left: 50px;
    margin-top: 4px;
    text-decoration: none;
  }

  .example-link:hover,
  .example-link:focus {
    color: ${primary};
    text-decoration: underline;
  }
`;

const modalStyles = css`
  .bs4-modal-body {
    padding: ${doubleSpacing}px;
  }
`;

const buttonsSectionStyles = css`
  display: flex;
  justify-content: center;
  margin-top: 65px;
`;

const PersonalizedTakeawaysSettingsModal = ({
  show,
  onClose,
  isAiCapabilitiesEnabled,
  initialEnabled,
  catalogId,
  courseAlias,
  onSave,
}: PersonalizedTakeawaysSettingsModalProps) => {
  const { injectServices } = React.useContext(AngularContext);
  const [$state] = injectServices(['$state']);
  const dispatch = useAppDispatch();
  const [enabledDraft, setEnabledDraft] = useState(initialEnabled);
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = React.useRef(false);

  React.useEffect(() => {
    if (show) {
      setEnabledDraft(initialEnabled);
    }
  }, [show, initialEnabled]);

  const isDirty = useMemo(
    () => enabledDraft !== initialEnabled,
    [enabledDraft, initialEnabled],
  );

  const handleSave = () => {
    if (!isDirty || isSavingRef.current) return;

    isSavingRef.current = true;
    setIsSaving(true);
    dispatch(savePersonalizedTakeawaysSettings({
      catalogId,
      enabled: enabledDraft,
    }))
      .then((resultAction: { error?: unknown }) => {
        if (!resultAction?.error) {
          onSave(enabledDraft);
          onClose();
        }
      })
      .finally(() => {
        isSavingRef.current = false;
        setIsSaving(false);
      });
  };

  const onSeeExampleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    onClose();
    $state.go('course-personalized-takeaways', {
      catalogId,
      from: $state.current.name,
    });
  };

  const body = (
    <div css={modalBodyStyles}>
      <div>
        <div className='header-row'>
          {isAiCapabilitiesEnabled
            ? <GradientAiIcon icon='gen-ai' size='small' className='mr-2' />
            : <NvIcon icon='gen-ai' size='small' className='mr-2 text-gray-4' />}
          <span className={`text-large-regular font-weight-bold${isAiCapabilitiesEnabled ? '' : ' text-gray-4'}`}>
            {t.COURSE_ADMIN_MEGA_MENU.PERSONALIZED_TAKEAWAYS.MODAL.HEADER()}
          </span>
        </div>

        {!isAiCapabilitiesEnabled && (
          <div className='disabled-alert text-small font-weight-bold'>
            <NvIcon icon='info' size='small' className='mr-1' />
            {t.COURSE_ADMIN_MEGA_MENU.PERSONALIZED_TAKEAWAYS.MODAL.AI_DISABLED_ALERT()}
          </div>
        )}

        <div className='description text-large-body'>
          {t.COURSE_ADMIN_MEGA_MENU.PERSONALIZED_TAKEAWAYS.MODAL.DESCRIPTION({ courseAlias })}
        </div>

        <div className='switch-row'>
          <NvSwitch
            checked={enabledDraft}
            onChange={setEnabledDraft}
            dataQa={config.pendo.aiLearning.togglePersonalizedTakeaways}
            disabled={!isAiCapabilitiesEnabled}
            ariaLabel={t.COURSE_ADMIN_MEGA_MENU.PERSONALIZED_TAKEAWAYS.MODAL.SWITCH_LABEL({ courseAlias })}
          />
          <span className={`text-medium ml-2${isAiCapabilitiesEnabled ? '' : ' text-gray-4'}`}>{t.COURSE_ADMIN_MEGA_MENU.PERSONALIZED_TAKEAWAYS.MODAL.SWITCH_LABEL({ courseAlias })}</span>
        </div>
        <a
          href='#'
          className='example-link text-medium'
          data-qa={config.pendo.aiLearning.seeExamplePersonalizedTakeaways}
          onClick={onSeeExampleClick}
        >
          {t.COURSE_ADMIN_MEGA_MENU.PERSONALIZED_TAKEAWAYS.MODAL.SEE_AN_EXAMPLE()}
        </a>

        <div css={buttonsSectionStyles}>
          <Button type='button' className='mr-2' variant='secondary' data-qa={config.pendo.aiLearning.cancelPersonalizedTakeaways} onClick={onClose} disabled={isSaving}>{t.FORM.CANCEL()}</Button>
          <Button type='button' variant='primary' data-qa={config.pendo.aiLearning.savePersonalizedTakeaways} onClick={handleSave} disabled={!isDirty || isSaving}>{isSaving ? t.FORM.SAVING() : t.FORM.SAVE()}</Button>
        </div>
      </div>
    </div>
  );

  return (
    <NvModal
      show={show}
      type={ModalType.FIXED}
      width={600}
      header={t.COURSE_ADMIN_MEGA_MENU.PERSONALIZED_TAKEAWAYS.MODAL.TITLE()}
      onClose={onClose}
      body={body}
      modalStyles={modalStyles}
    />
  );
};

export default PersonalizedTakeawaysSettingsModal;
